import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

const TissueCulture = () => {
  const navigate = useNavigate();
  const [tcPlants, setTcPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tcSearch, setTcSearch] = useState('');
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase.from('plants').select('common_name, sold_price, image_url, genus, tissue_culture_sale_pack_size, date_sold').eq('status', 'approved').ilike('sale_type', '%tissue%').order('id', { ascending: false }).limit(100);
      if (data) {
        const seen = new Set();
        const unique = data.filter(p => { if (seen.has(p.common_name)) return false; seen.add(p.common_name); return true; });
        setTcPlants(unique);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const formatPrice = (val) => {
    if (!val) return '—';
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? val : '$' + num.toFixed(2);
  };

  const filteredTcPlants = tcPlants.filter(p => p.common_name.toLowerCase().includes(tcSearch.toLowerCase()));
  const displayedTcPlants = tcSearch ? filteredTcPlants : filteredTcPlants.slice(0, 5);

  useEffect(() => {
    document.title = 'Tissue Culture Plants - What They Are & Real Prices | True Plant Value';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Learn what tissue culture plants are and see real sold prices from collectors. Compare TC vs cuttings vs potted plants.');
    return () => { document.title = 'True Plant Value'; };
  }, []);

  return (
    <div style={{ paddingBottom: '80px' }}>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)', padding: 'clamp(48px, 8vw, 80px) 24px', textAlign: 'center', borderRadius: '0 0 40px 40px', marginBottom: '60px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '6px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '16px', fontFamily: 'Lato, sans-serif' }}>🧫 Plant Science</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '36px' : '56px', color: '#f5f0e8', fontWeight: 700, marginBottom: '16px', lineHeight: 1.1 }}>Tissue Culture Plants</h1>
        <p style={{ color: 'rgba(245,240,232,0.8)', fontFamily: 'Lato, sans-serif', fontSize: '18px', maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 }}>
          What they are, why they matter, and what they're actually selling for.
        </p>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>

        {/* What is TC */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '40px', marginBottom: '60px', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>What Is TC?</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: '#1e3d20', marginBottom: '16px' }}>Grown in a Lab, Not a Greenhouse</h2>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '15px', color: '#4a5e4b', lineHeight: 1.8, marginBottom: '16px' }}>
              Tissue culture (TC) is a method of propagating plants from tiny pieces of plant tissue, such as cells, shoot tips, or leaf sections, in a sterile lab environment using special nutrients and hormones.
            </p>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '15px', color: '#4a5e4b', lineHeight: 1.8, marginBottom: '16px' }}>
              The result is thousands of genetically identical, disease-free plants that can be produced much faster than traditional methods like cuttings or division.
            </p>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '15px', color: '#4a5e4b', lineHeight: 1.8 }}>
              TC plants are typically sold in small packs or jars, still in their agar growing medium. They need to be acclimatized to normal growing conditions before potting up.
            </p>
          </div>
          <div style={{ background: '#f0f7f1', borderRadius: '24px', padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🧫</div>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1e3d20', marginBottom: '12px' }}>TC at a Glance</h3>
            {[
              { icon: '✅', text: 'Disease and pest free' },
              { icon: '✅', text: 'Genetically true to parent' },
              { icon: '✅', text: 'Often more affordable than cuttings' },
              { icon: '⚠️', text: 'Requires acclimatization period' },
              { icon: '⚠️', text: 'Takes longer to reach mature size' },
              { icon: '⚠️', text: 'Variegation not always guaranteed' },
            ].map(({ icon, text }) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid rgba(143,185,154,0.2)', textAlign: 'left' }}>
                <span style={{ fontSize: '16px' }}>{icon}</span>
                <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#4a5e4b' }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* TC vs Cuttings */}
        <div style={{ background: '#f7f3ec', borderRadius: '24px', padding: '40px', marginBottom: '60px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#1e3d20', marginBottom: '24px', textAlign: 'center' }}>TC vs. Cuttings vs. Potted — What's the Difference?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
            {[
              { type: 'Tissue Culture', icon: '🧫', pros: ['Cheapest entry price', 'Disease free', 'Mass produced'], cons: ['Needs acclimatization', 'Tiny to start', 'Takes 6-18 months to mature'] },
              { type: 'Cutting', icon: '✂️', pros: ['Faster to mature', 'Established roots', 'See variegation immediately'], cons: ['More expensive', 'Risk of disease', 'Less supply'] },
              { type: 'Potted', icon: '🪴', pros: ['Ready to grow', 'Established plant', 'Lowest risk'], cons: ['Most expensive', 'Harder to ship', 'Biggest size'] },
            ].map(({ type, icon, pros, cons }) => (
              <div key={type} style={{ background: '#fff', borderRadius: '16px', padding: '24px' }}>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '8px' }}>{icon}</div>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20' }}>{type}</h3>
                </div>
                {pros.map(p => <div key={p} style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a7c59', marginBottom: '6px' }}>✅ {p}</div>)}
                {cons.map(c => <div key={c} style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#c0392b', marginBottom: '6px' }}>✕ {c}</div>)}
              </div>
            ))}
          </div>
        </div>

        {/* TC Price Data */}
        <div style={{ marginBottom: '60px' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Real Market Data</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20' }}>Browse TC Plant Prices</h2>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '15px', color: '#4a5e4b', marginTop: '12px' }}>
              {tcPlants.length} tissue culture varieties tracked — click any plant to see full price history.
            </p>
            <div style={{ maxWidth: '440px', margin: '20px auto 0' }}>
              <input type="text" value={tcSearch} onChange={(e) => setTcSearch(e.target.value)}
                placeholder="Search TC plants..."
                style={{ width: '100%', padding: '12px 18px', border: '2px solid #c4a882', borderRadius: '14px', fontSize: '15px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontStyle: 'italic' }}>Loading TC plants...</p>
          ) : (
            <>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: '16px' }}>
              {displayedTcPlants.map((plant) => (
                <div key={plant.common_name} onClick={() => navigate(`/plant-details/${encodeURIComponent(plant.common_name)}`)}
                  style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(44,74,46,0.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(44,74,46,0.08)'; }}
                >
                  {plant.image_url
                    ? <img src={plant.image_url} alt={plant.common_name} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                    : <div style={{ height: '120px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px' }}>🧫</div>
                  }
                  <div style={{ padding: '12px' }}>
                    <div style={{ fontSize: '9px', color: '#8b6914', fontFamily: 'Lato, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{plant.genus || 'TC Plant'}</div>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '13px', color: '#1e3d20', margin: '0 0 6px', lineHeight: 1.3 }}>{plant.common_name}</h3>
                    <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a7c59', fontWeight: 700 }}>{formatPrice(plant.sold_price)}</div>
                  </div>
                </div>
              ))}
            </div>
            {!tcSearch && filteredTcPlants.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <button onClick={() => navigate('/search?type=Tissue Culture')} style={{ background: 'transparent', border: '2px solid #4a7c59', color: '#4a7c59', borderRadius: '24px', padding: '12px 32px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                  Browse All {tcPlants.length} Tissue Culture Plants →
                </button>
              </div>
            )}
            {tcSearch && filteredTcPlants.length === 0 && (
              <p style={{ textAlign: 'center', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontStyle: 'italic' }}>No TC plants found matching "{tcSearch}".</p>
            )}
            </>
          )}
        </div>

        {/* CTA */}
        <div style={{ background: 'linear-gradient(135deg, #1e3d20, #4a7c59)', borderRadius: '24px', padding: '48px 32px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#f5f0e8', marginBottom: '12px' }}>Bought or Sold a TC Plant?</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '15px', color: '#8fb99a', marginBottom: '28px', maxWidth: '400px', margin: '0 auto 28px' }}>Help the community know what TC plants are really worth. Submit your sale in 60 seconds.</p>
          <button onClick={() => navigate('/submit')} style={{ background: '#f5f0e8', color: '#1e3d20', border: 'none', borderRadius: '24px', padding: '14px 36px', fontSize: '14px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
            Submit a TC Sale
          </button>
        </div>

      </div>
    </div>
  );
};

export default TissueCulture;